import type { ReactNode } from "react";
import { DataTable } from "../components/DataTable/index.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { getCols } from "../util/getCols.ts";
import type { View } from "../../../../common/types.ts";
import { wrappedContractAmount, wrappedContractExp } from "../components/contract.tsx";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import type { DataTableRow } from "../components/DataTable/index.tsx";
import { Trans, i18n } from "../util/useT.ts";
import { wrappedTeamAbbrevLink } from "../components/TeamAbbrevLink.tsx";
import { wrappedPosition } from "../util/positionNames.tsx";
import { SkillsBlock } from "../components/SkillsBlock.tsx";
import {
  MobileContentHero,
  MobileEntityRow,
  MobileExpertDisclosure,
} from "../mobile/mobilePrimitives.tsx";
import {
  canShowP3V1Rating,
  getP3V1TopRatingPlayers,
  getP3V1UserTeamRatingMarker,
} from "../util/rankingDensity.ts";
import { isCountHighlightRenderable } from "../mobile/statMeaningfulness.ts";
import { MarketRatingStatusMetric } from "./MarketRatingStatusMetric.tsx";

type DataTableCellObject = {
  value: ReactNode;
};

const isDataTableCellObject = (value: DataTableRow["data"][number]): value is DataTableCellObject =>
  typeof value === "object" && value !== null && Object.hasOwn(value, "value");

const getCellValue = (value: DataTableRow["data"][number]): ReactNode =>
  isDataTableCellObject(value) ? value.value : (value as ReactNode);

const PlayerRatings = ({
  abbrev,
  challengeNoRatings,
  currentSeason,
  players,
  season,
  userTid,
}: View<"playerRatings">) => {
  useTitleBar({
    title: i18n.t("playerRatings.title", { ns: "ui", defaultValue: "Player Ratings" }),
    jumpTo: !window.mobile,
    jumpToSeason: season,
    dropdownView: "player_ratings",
    dropdownFields: { teamsAndAllWatchPlayoffs: abbrev, seasons: season },
  });

  const cols = getCols(["Name", "Pos", "Team", "Age", "Contract", "Exp", "Ovr", "Pot", "Skills"]);

  const rows: DataTableRow[] = players.map((p) => {
    const showRatings = canShowP3V1Rating(p, challengeNoRatings);

    return {
      key: p.pid,
      metadata: {
        type: "player",
        pid: p.pid,
        season,
        playoffs: "regularSeason",
      },
      data: [
        wrappedPlayerNameLabels({
          pid: p.pid,
          injury: p.injury,
          season,
          skills: p.ratings.skills,
          jerseyNumber: p.stats.jerseyNumber,
          defaultWatch: p.watch,
          firstName: p.firstName,
          firstNameShort: p.firstNameShort,
          lastName: p.lastName,
          awards: p.awards,
          awardsSeason: season,
          nameOnly: true,
        }),
        wrappedPosition(p.ratings.pos),
        wrappedTeamAbbrevLink({
          tid: p.stats.tid,
          abbrev: p.stats.abbrev,
          season,
        }),
        p.age,
        p.contract.amount > 0 ? wrappedContractAmount(p) : null,
        p.contract.amount > 0 && season === currentSeason ? wrappedContractExp(p) : null,
        showRatings ? p.ratings.ovr : null,
        showRatings ? p.ratings.pot : null,
        showRatings ? (
          <SkillsBlock
            className="skills-alone"
            host="player-ratings"
            skills={p.ratings.skills}
          />
        ) : null,
      ],
      classNames: {
        "table-danger": p.hof,
        "table-info": p.stats.tid === userTid,
      },
    };
  });
  const topRatingPlayers = getP3V1TopRatingPlayers(players, challengeNoRatings, 8);
  const userTeamRatingMarker = getP3V1UserTeamRatingMarker(players, userTid, challengeNoRatings);
  const top25DefaultCount = Math.min(players.length, 25);
  const ratingsScope = abbrev === undefined || abbrev === "all" ? "all-league" : "team";

  // B1 content-first migration (v5Content contract): the first screen IS the
  // top-rated rows themselves. The old data-story template eyebrow/subtitle and
  // the archive-hero description narration are gone (法则一); the only guidance
  // is this single scope explainer line.
  const ratingsExplainer =
    ratingsScope === "team"
      ? i18n.t("playerRatings.mobileExplainerTeam", {
          ns: "ui",
          defaultValue: "Your team's highest-rated players lead the board.",
        })
      : i18n.t("playerRatings.mobileExplainerAll", {
          ns: "ui",
          defaultValue: "The league's highest-rated players lead the board.",
        });

  const renderPlayerName = (p: (typeof players)[number]) =>
    getCellValue(
      wrappedPlayerNameLabels({
        pid: p.pid,
        injury: p.injury,
        season,
        skills: p.ratings.skills,
        jerseyNumber: p.stats.jerseyNumber,
        defaultWatch: p.watch,
        firstName: p.firstName,
        firstNameShort: p.firstNameShort,
        lastName: p.lastName,
        awards: p.awards,
        awardsSeason: season,
        nameOnly: true,
      }),
    );

  const renderPlayerMeta = (p: (typeof players)[number]) => (
    <>
      {getCellValue(wrappedPosition(p.ratings.pos))}{" "}
      {getCellValue(
        wrappedTeamAbbrevLink({
          tid: p.stats.tid,
          abbrev: p.stats.abbrev,
          season,
        }),
      )}
    </>
  );

  return (
    <div
      data-desktop-production-view="playerRatings"
    >
      {/* G2e: the data-center hub tab strip retired; a single breadcrumb back to
          the converged Data entry (League Leaders) replaces it. */}
      <a
        className="btn btn-link btn-sm px-0 mb-2 d-inline-flex align-items-center gap-1"
        data-data-return-link=""
        href={helpers.leagueUrl(["leaders"])}
      >
        <span aria-hidden="true">←</span>
        {i18n.t("dataHub.returnToData", { ns: "ui", defaultValue: "Back to data" })}
      </a>

      {challengeNoRatings ? (
        <p className="alert alert-danger d-inline-block">
          <b>{i18n.t("common.challengeMode", { ns: "ui", defaultValue: "Challenge Mode:" })}</b>{" "}
          {i18n.t("playerTable.hiddenRatings", {
            ns: "ui",
            defaultValue: "All player ratings are hidden, except for retired players.",
          })}
        </p>
      ) : null}

      <MobileContentHero
        className="mb-3"
        data-top-player-summary=""
        data-top-25-default={top25DefaultCount}
        data-v5-playerratings-rating-board-first-screen=""
        explainer={ratingsExplainer}
        title={
          challengeNoRatings
            ? i18n.t("playerRatings.mobileHeroHiddenTitle", {
                ns: "ui",
                defaultValue: "Ratings are hidden in this league",
              })
            : undefined
        }
      >
        <div
          className="d-flex flex-wrap gap-2 mb-3"
        >
          <span className="badge text-bg-light border">
            {i18n.t("playerRatings.p1s07StarterCategory", {
              ns: "ui",
              defaultValue: "Starter-ready",
            })}
          </span>
          <span className="badge text-bg-light border">
            {i18n.t("playerRatings.p1s07TradeValueCategory", {
              ns: "ui",
              defaultValue: "Trade value",
            })}
          </span>
          <span className="badge text-bg-light border">
            {i18n.t("playerRatings.p1s07PotentialCategory", {
              ns: "ui",
              defaultValue: "Potential stars",
            })}
          </span>
        </div>

        <section
          data-seven-to-eight-visible-rows={topRatingPlayers.length}
          data-user-team-rating-marker={userTeamRatingMarker?.rank ?? "none"}
        >
          {topRatingPlayers.map((p, index) => (
            <MobileEntityRow
              key={p.pid}
              data-user-team-rating-marker={p.stats.tid === userTid ? "" : undefined}
              highlighted={p.stats.tid === userTid}
              rank={`#${index + 1}`}
              title={renderPlayerName(p)}
              meta={renderPlayerMeta(p)}
              detail={
                <MarketRatingStatusMetric
                  className="mt-2 text-start"
                  hidden={!canShowP3V1Rating(p, challengeNoRatings)}
                  label={i18n.t("marketMetrics.playerLevel", {
                    ns: "ui",
                    defaultValue: "Player level",
                  })}
                  potential={p.ratings.pot}
                  rating={p.ratings.ovr}
                />
              }
            />
          ))}
        </section>

        {/* A3: count-0 highlight rows do not render. */}
        {userTeamRatingMarker && isCountHighlightRenderable(userTeamRatingMarker ? 1 : 0) ? (
          <div
            className="mt-2"
            data-best-team-rating=""
          >
            <MarketRatingStatusMetric
              hidden={!canShowP3V1Rating(userTeamRatingMarker.player, challengeNoRatings)}
              label={i18n.t("playerRatings.mobileBestTeamRating", {
                ns: "ui",
                defaultValue: "Best team player",
              })}
              potential={userTeamRatingMarker.player.ratings.pot}
              rating={userTeamRatingMarker.player.ratings.ovr}
            />
            <div className="small text-body-secondary mt-1">
              #{userTeamRatingMarker.rank} · {renderPlayerName(userTeamRatingMarker.player)}
            </div>
          </div>
        ) : null}
      </MobileContentHero>

      <MobileExpertDisclosure
        className="mb-3"
        expertHostId="player-ratings-complete-table"
        count={i18n.t("playerRatings.mobileAdvancedCount", {
          ns: "ui",
          defaultValue: "{{count}} players",
          count: players.length,
        })}
        data-advanced-table-depth=""
        summary={i18n.t("playerRatings.mobileAdvancedSummary", {
          ns: "ui",
          defaultValue: "Complete ratings table",
        })}
      >
        <p>
          <Trans
            i18nKey="playerTable.highlights"
            ns="ui"
            defaults="Players on your team are <blue>highlighted in blue</blue>. Players in the Hall of Fame are <red>highlighted in red</red>."
            components={{
              blue: <span className="text-info" />,
              red: <span className="text-danger" />,
            }}
          />
        </p>
        <DataTable
          cols={cols}
          defaultSort={[6, "desc"]}
          defaultStickyCols={window.mobile ? 0 : 1}
          mobileP3Export={{
            label: i18n.t("playerRatings.title", {
              ns: "ui",
              defaultValue: "Player Ratings",
            }),
            season,
          }}
          name="PlayerRatings"
          pagination
          rows={rows}
        />
      </MobileExpertDisclosure>
    </div>
  );
};

export default PlayerRatings;
